import { Button, IconButton, Tooltip } from '@affine/component';
import { MenuItem } from '@affine/component/ui/menu';
import {
  EditorService,
  PresentationStateMachine,
} from '@affine/core/modules/editor';
import { useI18n } from '@affine/i18n';
import { PresentationIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useEffect, useRef, useState } from 'react';

export const DetailPageHeaderPresentButton = ({
  rail = false,
  railLabel,
  showTooltip = true,
}: {
  rail?: boolean;
  railLabel?: string;
  showTooltip?: boolean;
} = {}) => {
  const t = useI18n();
  const editorService = useService(EditorService);
  const stateMachine = useService(PresentationStateMachine);
  const isFollowing = useLiveData(stateMachine.isFollowing$);
  const isPresenting = useLiveData(editorService.editor.isPresenting$);
  const labelRef = useRef<HTMLSpanElement>(null);
  const [labelTruncated, setLabelTruncated] = useState(false);

  useEffect(() => {
    const label = labelRef.current;
    if (!label) {
      setLabelTruncated(false);
      return;
    }
    const measureLabel = () =>
      setLabelTruncated(label.scrollWidth > label.clientWidth);
    measureLabel();
    const observer = new ResizeObserver(measureLabel);
    observer.observe(label);
    return () => observer.disconnect();
  }, [railLabel]);

  const handleClick = () => {
    if (!isFollowing) {
      editorService.editor.togglePresentation();
    }
  };

  const button = railLabel ? (
    <Button
      data-testid="detail-header-present-button"
      data-active={isPresenting}
      data-rail={rail || undefined}
      style={{ flexShrink: 0 }}
      size="custom"
      variant="custom"
      prefix={<PresentationIcon />}
      prefixStyle={rail ? { color: 'currentColor' } : undefined}
      disabled={isFollowing}
      onClick={handleClick}
    >
      <span ref={labelRef} data-rail-action-label>
        {railLabel}
      </span>
    </Button>
  ) : (
    <IconButton
      data-testid="detail-header-present-button"
      data-active={isPresenting}
      data-rail={rail || undefined}
      style={{ flexShrink: 0 }}
      size={rail ? '20' : '24'}
      iconStyle={rail ? { color: 'currentColor' } : undefined}
      disabled={isFollowing}
      onClick={handleClick}
    >
      <PresentationIcon />
    </IconButton>
  );

  return showTooltip || labelTruncated ? (
    <Tooltip
      content={t['com.streamlain.presentation.button.start']()}
      side={rail ? 'right' : undefined}
    >
      {button}
    </Tooltip>
  ) : (
    button
  );
};

export const DetailPageHeaderPresentMenuItem = () => {
  const t = useI18n();
  const editorService = useService(EditorService);
  const stateMachine = useService(PresentationStateMachine);
  const mode = useLiveData(editorService.editor.mode$);
  const isFollowing = useLiveData(stateMachine.isFollowing$);

  if (mode !== 'edgeless') {
    return null;
  }

  return (
    <MenuItem
      prefixIcon={<PresentationIcon />}
      disabled={isFollowing}
      onSelect={() => {
        if (!isFollowing) {
          editorService.editor.togglePresentation();
        }
      }}
    >
      {t['com.streamlain.presentation.button.start']()}
    </MenuItem>
  );
};
